class ProductDetailsTabs extends HTMLElement {
  constructor() {
    super();

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleHashChange = this.handleHashChange.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
  }

  connectedCallback() {
    this.tabList = this.querySelector('[role="tablist"]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"][data-product-details-tab]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"][data-product-details-panel]'));

    if (!this.tabList || this.tabs.length === 0 || this.panels.length === 0) return;

    this.tabList.addEventListener('click', this.handleClick);
    this.tabList.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('hashchange', this.handleHashChange);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);

    const hashTab = this.getTabFromHash();
    const initialTab = hashTab || this.dataset.activeTab || this.tabs[0].dataset.productDetailsTab;

    this.classList.add('is-ready');
    this.activate(initialTab, { focus: false });
  }

  disconnectedCallback() {
    this.tabList?.removeEventListener('click', this.handleClick);
    this.tabList?.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('hashchange', this.handleHashChange);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
  }

  handleClick(event) {
    const tab = event.target.closest('[role="tab"][data-product-details-tab]');
    if (!tab || !this.contains(tab)) return;

    this.activate(tab.dataset.productDetailsTab, { focus: false });
  }

  handleKeydown(event) {
    const currentTab = event.target.closest('[role="tab"][data-product-details-tab]');
    if (!currentTab) return;

    const currentIndex = this.tabs.indexOf(currentTab);
    if (currentIndex < 0) return;

    let nextIndex = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(this.tabs[nextIndex].dataset.productDetailsTab, { focus: true });
  }

  handleHashChange() {
    const tabName = this.getTabFromHash();
    if (tabName) this.activate(tabName, { focus: false });
  }

  handleBlockSelect(event) {
    if (String(event.detail?.sectionId) !== String(this.dataset.sectionId)) return;

    const block = event.target.closest('[data-product-details-tab-block]');
    const tabName = block?.dataset.productDetailsTabBlock;

    if (tabName) this.activate(tabName, { focus: false });
  }

  getTabFromHash() {
    if (!window.location.hash) return null;

    const panel = this.querySelector(window.location.hash);
    return panel?.dataset.productDetailsPanel || null;
  }

  activate(tabName, { focus = false } = {}) {
    const activeTab = this.tabs.find((tab) => tab.dataset.productDetailsTab === tabName);
    const activePanel = this.panels.find((panel) => panel.dataset.productDetailsPanel === tabName);

    if (!activeTab || !activePanel) return;

    this.tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      const isActive = panel === activePanel;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    this.dataset.activeTab = tabName;

    if (focus) activeTab.focus({ preventScroll: true });
  }
}

if (!customElements.get('product-details-tabs')) {
  customElements.define('product-details-tabs', ProductDetailsTabs);
}
